var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faTableColumns } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, Divider, IconButton, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { useColumnPreferences } from './useColumnPreferences';
import { useDraggableColumns } from './useDraggableColumns';
export var ColumnCustomizer = function (_a) {
  var columns = _a.columns,
    lockedField = _a.lockedField,
    order = _a.order,
    hidden = _a.hidden,
    onUpdate = _a.onUpdate,
    onReset = _a.onReset,
    customizeLabel = _a.customizeLabel,
    resetLabel = _a.resetLabel;
  var _b = useState(),
    anchorEl = _b[0],
    setAnchorEl = _b[1];
  var open = !!anchorEl;
  var lockedColumn = lockedField ? columns.find(function (c) {
    return c.field === lockedField;
  }) : undefined;
  var otherColumns = order.filter(function (f) {
    return f !== lockedField;
  }).map(function (f) {
    return columns.find(function (c) {
      return c.field === f;
    });
  }).filter(Boolean);
  var handleToggle = function (field) {
    var newHidden = hidden.includes(field) ? hidden.filter(function (f) {
      return f !== field;
    }) : __spreadArray(__spreadArray([], hidden, true), [field], false);
    onUpdate(order, newHidden);
  };
  var handleClose = function () {
    return setAnchorEl(undefined);
  };
  return _jsxs(_Fragment, {
    children: [_jsx(Tooltip, __assign({
      title: customizeLabel,
      placement: "bottom"
    }, {
      children: _jsx(IconButton, __assign({
        size: "small",
        onClick: function (e) {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        },
        sx: {
          color: 'inherit',
          p: 0.5
        },
        "aria-label": customizeLabel
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: faTableColumns,
          style: {
            display: 'block'
          }
        })
      }))
    })), _jsxs(Menu, __assign({
      anchorEl: anchorEl,
      open: open,
      onClose: handleClose,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      },
      slotProps: {
        paper: {
          sx: {
            minWidth: 220
          }
        }
      }
    }, {
      children: [lockedColumn && _jsxs(MenuItem, __assign({
        disabled: true,
        dense: true
      }, {
        children: [_jsx(Checkbox, {
          checked: true,
          disabled: true,
          size: "small",
          sx: {
            p: 0.5,
            mr: 1
          }
        }), _jsx(ListItemText, {
          primary: lockedColumn.headerName
        })]
      })), otherColumns.map(function (col) {
        return _jsxs(MenuItem, __assign({
          dense: true,
          onClick: function () {
            return handleToggle(col.field);
          }
        }, {
          children: [_jsx(Checkbox, {
            checked: !hidden.includes(col.field),
            size: "small",
            sx: {
              p: 0.5,
              mr: 1,
              pointerEvents: 'none'
            },
            tabIndex: -1
          }), _jsx(ListItemText, {
            primary: col.headerName
          })]
        }), col.field);
      }), _jsx(Divider, {}), _jsx(MenuItem, __assign({
        dense: true,
        onClick: function () {
          onReset();
          handleClose();
        }
      }, {
        children: _jsx(ListItemText, {
          primary: resetLabel
        })
      }))]
    }))]
  });
};
/**
 * Wires column hide/show + drag-reorder persistence into a DataGrid's columns.
 * Returns the final column list: a leading column hosting the customizer button,
 * followed by the visible data columns in the user's saved order. Draggable headers
 * are attached automatically to every column except `lockedField`.
 */
export var useCustomizableColumns = function (_a) {
  var tableKey = _a.tableKey,
    defaultFields = _a.defaultFields,
    defaultHidden = _a.defaultHidden,
    lockedField = _a.lockedField,
    columns = _a.columns,
    customizeLabel = _a.customizeLabel,
    resetLabel = _a.resetLabel,
    customizerColumn = _a.customizerColumn;
  var _b = useColumnPreferences(tableKey, defaultFields, defaultHidden),
    prefs = _b.prefs,
    update = _b.update,
    reset = _b.reset;
  var makeDraggableHeader = useDraggableColumns(prefs.order, prefs.hidden, update).makeDraggableHeader;
  var columnsWithHeaders = useMemo(function () {
    return columns.map(function (col) {
      if (col.field === lockedField) return col;
      var existingHeader = col.renderHeader;
      return __assign(__assign({}, col), {
        renderHeader: function (params) {
          var _a;
          return makeDraggableHeader(col.field, existingHeader ? existingHeader(params) : (_a = col.headerName) !== null && _a !== void 0 ? _a : '');
        }
      });
    });
  }, [columns, lockedField, makeDraggableHeader]);
  var customizerColumnDefs = useMemo(function () {
    return columns.map(function (col) {
      var _a;
      return {
        field: col.field,
        headerName: (_a = col.headerName) !== null && _a !== void 0 ? _a : ''
      };
    });
  }, [columns]);
  return useMemo(function () {
    var orderedVisible = prefs.order.filter(function (field) {
      return !prefs.hidden.includes(field);
    }).map(function (field) {
      return columnsWithHeaders.find(function (col) {
        return col.field === field;
      });
    }).filter(Boolean);
    var leadingColumn = __assign(__assign({
      field: '__customizer__',
      headerName: '',
      width: 50,
      minWidth: 50,
      sortable: false,
      resizable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center'
    }, customizerColumn), {
      renderHeader: function () {
        return _jsx(ColumnCustomizer, {
          columns: customizerColumnDefs,
          lockedField: lockedField,
          order: prefs.order,
          hidden: prefs.hidden,
          onUpdate: update,
          onReset: reset,
          customizeLabel: customizeLabel,
          resetLabel: resetLabel
        });
      }
    });
    return __spreadArray([leadingColumn], orderedVisible, true);
  }, [columnsWithHeaders, customizerColumnDefs, prefs.order, prefs.hidden, update, reset, lockedField, customizeLabel, resetLabel, customizerColumn]);
};
export { useColumnPreferences } from './useColumnPreferences';
export { useDraggableColumns } from './useDraggableColumns';